// src/pages/stock/EsimBulkUploadDetail.tsx

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { confirmUpload } from '@apis/esimBulk/upload';
import type {
  GetUploadHistoriesResponse,
  GetUploadHistoryDetailsResponse,
} from '@apis/esimBulk/upload';

import { useUploadDetail } from '@queries/esimBulk/useUploadDetail';
import { useUploadDetails } from '@queries/esimBulk/useUploadDetails';
import { useUploadDetailsCount } from '@queries/esimBulk/useUploadDetailsCount';

import useAsyncEffect from '@hooks/useAsyncEffect';

import { timestampToDate } from '@utils/time';
import { Status, UploadStatus } from '@models/esim';

import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;

/* ------------------------------------------------------------------ */
/* Utilities                                                          */
/* ------------------------------------------------------------------ */

/** Format number with locale grouping (fallback to 0). */
const formatNumber = (n: number | undefined | null) =>
  Number(n ?? 0).toLocaleString();

/** Convert nullish/blank string to '-' placeholder. */
const dash = (v: unknown) => {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v.trim() ? v : '-';
  return String(v);
};

const HeaderLabel = ({ label, hint }: { label: string; hint?: string }) => {
  if (!hint) return <>{label}</>;
  return (
    <Tooltip title={hint} arrow>
      <Box component="span" sx={{ cursor: 'help' }}>
        {label}
      </Box>
    </Tooltip>
  );
};

/* ------------------------------------------------------------------ */
/* Chips                                                              */
/* ------------------------------------------------------------------ */

const UploadStatusChip = ({ status }: { status: UploadStatus }) => {
  switch (status) {
    case UploadStatus.OK:
      return <Chip size="small" label="OK" color="primary" variant="outlined" />;
    case UploadStatus.DUPLICATE:
      return <Chip size="small" label="DUPLICATE" color="warning" variant="filled" />;
    case UploadStatus.ALREADY_EXIST:
      return <Chip size="small" label="ALREADY EXIST" color="error" variant="filled" />;
    default:
      return <Chip size="small" label={dash(status)} variant="outlined" />;
  }
};

const StatusChip = ({ status }: { status: Status }) => {
  switch (status) {
    case Status.COMPLETED:
      return <Chip size="small" label="COMPLETED" color="success" variant="filled" />;
    case Status.PROCESSING:
      return <Chip size="small" label="PROCESSING" color="primary" variant="outlined" />;
    case Status.PENDING:
      return <Chip size="small" label="PENDING" color="warning" variant="filled" />;
    default:
      return <Chip size="small" label={dash(status)} variant="outlined" />;
  }
};

/* ------------------------------------------------------------------ */
/* Page Component                                                     */
/* ------------------------------------------------------------------ */

/**
 * eSIM Bulk Upload Detail
 *
 * Responsibilities
 * - Fetch a single upload summary and its row-level details.
 * - Provide "Only Failure" filter and pagination.
 * - Expose a "Confirm Upload" action in the page header when status is PENDING.
 * - Provide row-level utilities (copy activation code).
 */
export const EsimBulkUploadDetail = () => {
  const { uploadId } = useParams();
  const navigate = useNavigate();

  const { setPageHeaderSlots } = usePageHeader();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const uploadIdNumber = useMemo(() => Number(uploadId), [uploadId]);

  const [onlyFailed, setOnlyFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [isConfirming, setIsConfirming] = useState(false);

  const tableTopRef = useRef<HTMLDivElement | null>(null);

  // Local summary state to render while enabling header slots, summary card, etc.
  const [data, setData] = useState<GetUploadHistoriesResponse>({
    esimInventoryBulkUploadId: 0,
    createdAt: 0,
    filename: '',
    isDeleted: false,
    stock: { stockCode: '', stockName: '' },
    updatedAt: 0,
    okayCount: 0,
    failedCount: 0,
    totalCount: 0,
    status: Status.PENDING,
  });

  // Queries
  const { data: uploadDetail, isLoading: isUploadLoading } =
    useUploadDetail(uploadIdNumber);

  const { data: uploadDetailsData, isLoading: isDetailsLoading } = useUploadDetails({
    uploadId: uploadIdNumber,
    page,
    pageSize: PAGE_SIZE,
    params: { onlyFailed },
  });

  const { data: totalCount, isLoading: isCountLoading } =
    useUploadDetailsCount(uploadIdNumber);

  /**
   * Derive table rows from the latest list.
   * The backend `onlyFailed=true` may not filter perfectly yet;
   * defensively filter on the client when toggle is active.
   *
   * Note: `useMemo` depends on `uploadDetailsData?.data` directly to avoid
   * creating an intermediate `rawRows` binding that would trigger ESLint warnings.
   */
  const rows = useMemo<GetUploadHistoryDetailsResponse[]>(() => {
    const list = uploadDetailsData?.data ?? [];
    if (!onlyFailed) return list;
    return list.filter((r) => r.uploadStatus !== UploadStatus.OK);
  }, [onlyFailed, uploadDetailsData?.data]);

  const isTableLoading = Boolean(isUploadLoading || isDetailsLoading || isCountLoading);

  // Table columns: index, redemptionCode, serialNo, iccid, imsi, msisdn, lpaUrl, confirmationCode, pin, puk, expireAt, uploadStatus
  const columnsCount = 12;

  const head = useMemo(
    () => (
      <TableRow>
        <TableCell align="right" sx={cellSx.nowrap}>
          <HeaderLabel label="#" hint="Index (#)" />
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          <HeaderLabel label="Redeem" hint="Redemption Code" />
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          <HeaderLabel label="Serial" hint="Serial No." />
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          ICCID
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          IMSI
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          MSISDN
        </TableCell>

        <TableCell align="left" sx={cellSx.nowrap}>
          <HeaderLabel label="Activation" hint="LPA URL (Activation Code)" />
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          <HeaderLabel label="Confirm" hint="Confirmation Code" />
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          PIN
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          PUK
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          <HeaderLabel label="Expiry" hint="Expire At (Expiry)" />
        </TableCell>

        <TableCell align="center" sx={cellSx.nowrap}>
          <HeaderLabel label="Status" hint="Upload Status" />
        </TableCell>
      </TableRow>
    ),
    [],
  );

  /** Toggle "Only Failure" filter and scroll back to table top. */
  const handleToggleOnlyFailure = useCallback(() => {
    setOnlyFailed((prev) => !prev);
    setPage(1);

    requestAnimationFrame(() => {
      tableTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  /** Copy activation string to clipboard with user feedback. */
  const copyActivation = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        systemAlert.success('Activation copied.');
      } catch {
        systemAlert.error('Copy failed.');
      }
    },
    [systemAlert],
  );

  /** Confirm the entire upload (PENDING only). */
  const handleConfirm = useCallback(async () => {
    if (!Number.isFinite(uploadIdNumber) || uploadIdNumber <= 0) return;
    if (isConfirming) return;

    const ok = await confirm({
      priority: 'warning',
      title: 'Confirm this upload?',
      description:
        'Once confirmed, the uploaded inventory will be finalized and may not be editable.',
      confirmText: 'Confirm Upload',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    setIsConfirming(true);
    try {
      const { message } = await confirmUpload(uploadIdNumber);
      if (message !== 'OK') {
        systemAlert.error(message || 'Upload confirmation failed.');
        return;
      }

      systemAlert.success('Upload confirmed successfully.');
      navigate('/stocks/upload');
    } catch {
      systemAlert.error('Upload confirmation failed.');
    } finally {
      setIsConfirming(false);
    }
  }, [confirm, isConfirming, navigate, systemAlert, uploadIdNumber]);

  // Sync summary data when query result changes.
  useAsyncEffect(async () => {
    if (!uploadDetail) return;
    setData(uploadDetail.data[0]);
  }, [uploadDetail]);

  /**
   * Inject PageHeader slots:
   * - leading.title: "stock_code : stock_name" (stock_name de-emphasized)
   * - leading.description: "File: filename"
   * - actions: show "Confirm Upload" only when status is PENDING
   */
  useEffect(() => {
    const stockCode = data?.stock?.stockCode?.trim();
    const stockName = data?.stock?.stockName?.trim();
    const filename = data?.filename?.trim();

    const leadingTitle =
      stockCode || stockName ? (
        <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
          {stockCode && (
            <Typography component="span" variant="h6" fontWeight={900}>
              {stockCode}
            </Typography>
          )}
          {stockCode && stockName && (
            <Typography component="span" variant="h6" color="text.secondary">
              :
            </Typography>
          )}
          {stockName && (
            <Typography
              component="span"
              variant="subtitle1"
              fontWeight={700}
              color="text.secondary"
            >
              {stockName}
            </Typography>
          )}
        </Box>
      ) : undefined;

    const canConfirm =
      data.status === Status.PENDING &&
      Number.isFinite(uploadIdNumber) &&
      uploadIdNumber > 0;

    setPageHeaderSlots({
      leading: {
        title: leadingTitle,
        description: filename ? `File: ${filename}` : undefined,
      },
      actions: canConfirm
        ? [
            {
              key: 'confirm-upload',
              label: isConfirming ? 'Confirming…' : 'Confirm Upload',
              onClick: handleConfirm,
              variant: 'contained',
              primary: true,
              disabled: isConfirming,
            },
          ]
        : undefined,
    });
  }, [
    data.filename,
    data.status,
    data.stock.stockCode,
    data.stock.stockName,
    handleConfirm,
    isConfirming,
    setPageHeaderSlots,
    uploadIdNumber,
  ]);

  return (
    <>
      {/* Summary Card */}
      <Box mt={2}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>
              Upload Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stock, file and result overview for this upload job.
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Row 1: Stock Code, Stock Name, File */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Stock Code
              </Typography>
              {isUploadLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography variant="body2" fontWeight={800} sx={cellSx.nowrap}>
                  {dash(data.stock.stockCode)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Stock Name
              </Typography>
              {isUploadLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography variant="body2" fontWeight={700} sx={cellSx.nowrap}>
                  {dash(data.stock.stockName)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                File
              </Typography>
              {isUploadLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    ...cellSx.nowrap,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    display: 'block',
                  }}
                  title={data.filename}
                >
                  {dash(data.filename)}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Row 2: Total, OK, Fail */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              {isUploadLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography variant="body2" fontWeight={800} sx={cellSx.number}>
                  {formatNumber(data.totalCount)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                OK
              </Typography>
              {isUploadLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={800}
                  sx={{ ...cellSx.number, color: 'success.main' }}
                >
                  {formatNumber(data.okayCount)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Fail
              </Typography>
              {isUploadLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={(data.failedCount ?? 0) > 0 ? 900 : 700}
                  sx={{
                    ...cellSx.number,
                    color: (data.failedCount ?? 0) > 0 ? 'error.main' : 'text.secondary',
                  }}
                >
                  {formatNumber(data.failedCount)}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Row 3: Status, Created At */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              {isUploadLoading ? (
                <Skeleton height={28} />
              ) : (
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <StatusChip status={data.status} />
                  {(data.failedCount ?? 0) > 0 && (
                    <Typography variant="body2" fontWeight={800} sx={{ color: 'error.main' }}>
                      Failures detected
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Created At
              </Typography>
              {isUploadLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography variant="body2" fontWeight={700} sx={cellSx.nowrap}>
                  {data.createdAt ? timestampToDate(data.createdAt) : '-'}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Filter + Table */}
      <Box mt={2} ref={tableTopRef}>
        <Box
          mb={1}
          display="flex"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
        >
          <Typography variant="subtitle1" fontWeight={800}>
            Upload Details
          </Typography>

          <FormControlLabel
            control={
              <Switch checked={onlyFailed} onChange={handleToggleOnlyFailure} color="warning" />
            }
            label="Only Failure"
          />
        </Box>

        <StandardTableToolbar
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount ?? 0}
          onChangePage={setPage}
        />

        <StandardTableFrame<GetUploadHistoryDetailsResponse>
          head={head}
          rows={rows}
          getRowKey={(row) => row.esimInventoryBulkUploadDetailId}
          columnsCount={columnsCount}
          tableSx={{ minWidth: 1260 }}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          emptyText={onlyFailed ? 'No failed rows found.' : 'No upload details found.'}
          getRowStatus={(row) => (row.uploadStatus !== UploadStatus.OK ? 'error' : 'normal')}
          renderRow={(row, index) => {
            const activation = dash(row.lpaUrl);
            const canCopyActivation = activation !== '-';

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                <TableCell align="center" sx={cellSx.mono}>
                  {dash(row.redemptionCode)}
                </TableCell>

                <TableCell align="center" sx={cellSx.mono}>
                  {dash(row.serialNo)}
                </TableCell>

                <TableCell align="center" sx={cellSx.mono}>
                  {dash(row.iccid)}
                </TableCell>

                <TableCell align="center" sx={cellSx.mono}>
                  {dash(row.imsi)}
                </TableCell>

                <TableCell align="center" sx={cellSx.mono}>
                  {dash(row.msisdn)}
                </TableCell>

                {/* Activation: ellipsis + tooltip + copy (only when value exists) */}
                <TableCell
                  align="left"
                  sx={{
                    ...cellSx.nowrap,
                    maxWidth: 420,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                    {activation === '-' ? (
                      <Box component="span">-</Box>
                    ) : (
                      <Tooltip title={activation} arrow>
                        <Box
                          component="span"
                          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {activation}
                        </Box>
                      </Tooltip>
                    )}

                    {canCopyActivation && (
                      <Tooltip title="Copy" arrow>
                        <IconButton
                          size="small"
                          onClick={() => copyActivation(String(activation))}
                          aria-label="copy activation"
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>

                <TableCell align="center" sx={cellSx.mono}>
                  {dash(row.confirmationCode)}
                </TableCell>

                <TableCell align="center" sx={cellSx.mono}>
                  {dash(row.pin)}
                </TableCell>

                <TableCell align="center" sx={cellSx.mono}>
                  {dash(row.puk)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {row.expireAt ? timestampToDate(row.expireAt, 'YYYY-MM-DD') : '-'}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <UploadStatusChip status={row.uploadStatus} />
                </TableCell>
              </>
            );
          }}
        />

        <Box mt={2}>
          <StandardPagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount ?? 0}
            onChangePage={setPage}
          />
        </Box>
      </Box>
    </>
  );
};
